import React, { useState, useEffect, useCallback } from 'react';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
import {
  IconSearch,
  IconRefresh,
  IconFilter,
  IconCirclePlus,
  IconPencil,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAdmins,
  searchAdminUsers,
  deleteAdminAccounts
} from '../../redux/slices/adminsSlice';

import { useAdminLayout } from "../../context/adminLayoutUseContext";
import LoadingFallback from '../../components/common/LoadingFallback';
import ErrorFallback from '../../components/common/ErrorFallback';

// NavItem Component
function NavItem({ label, to }) {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <Link
      to={to}
      className={`px-3 py-2 rounded-md cursor-pointer flex items-center gap-2 ${isActive
        ? 'bg-[#FFD500] text-[#0B0C52] font-semibold'
        : 'hover:bg-white/10 text-white'
        }`}
    >
      {label}
    </Link>
  );
}

// IconButton Component
function IconButton({ children, onClick }) {
  return (
    <button
      onClick={onClick}
      className="p-1 hover:bg-gray-100 rounded transition"
    >
      {children}
    </button>
  );
}

// DeleteAdminModal Component
function DeleteAdminModal({ admin, onClose, onConfirm }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white w-[500px] max-w-full rounded-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          <IconX size={20} />
        </button>
        <h2 className="text-lg font-semibold text-red-600 mb-4 flex items-center gap-2">
          <IconTrash size={20} />
          Delete Admin
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Are you sure you want to permanently delete{' '}
          <strong>{admin.full_name || 'this admin'}</strong>?<br />
          This action cannot be undone.
        </p>
        <div className="flex gap-4">
          <button
            onClick={() => {
              onConfirm(admin.id);
              onClose();
            }}
            className="bg-red-500 text-white px-4 py-2 rounded hover:opacity-90"
          >
            Delete
          </button>
          <button
            onClick={onClose}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:opacity-80"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// FilterModal Component
function FilterModal({
  isOpen,
  onClose,
  role,
  setRole,
  loginDate,
  setLoginDate,
  loginTime,
  setLoginTime,
  onApply,
}) {
  if (!isOpen) return null;

  const handleReset = () => {
    setRole('');
    setLoginDate('');
    setLoginTime('');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white w-[400px] max-w-full rounded-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          <IconX size={20} />
        </button>
        <h2 className="text-lg font-semibold text-[#0B0C52] mb-4">Filter Options</h2>

        <div className="mb-4">
          <h3 className="text-sm font-semibold mb-2">Filter By Login</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Date</label>
              <input
                type="date"
                value={loginDate}
                onChange={(e) => setLoginDate(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 w-full"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Time</label>
              <input
                type="time"
                value={loginTime}
                onChange={(e) => setLoginTime(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 w-full"
              />
            </div>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-sm font-semibold mb-2">Filter By Role</h3>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Select Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 w-full"
            >
              <option value="">All Roles</option>
              <option value="super_admin">Super Admin</option>
              <option value="editor">Editor</option>
              <option value="manager">Manager</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <button
            onClick={() => {
              handleReset();
              onApply(true);
            }}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:opacity-80"
          >
            Reset
          </button>
          <button
            onClick={onClose}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:opacity-80"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onApply(false);
              onClose();
            }}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
}

// Main Component
function AdminAccountRoles() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setLayoutConfig } = useAdminLayout();

  const { loading, error, data: admins = [] } = useSelector((state) => state.admins);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalType, setModalType] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [roleFilter, setRoleFilter] = useState('');
  const [loginDateFilter, setLoginDateFilter] = useState('');
  const [loginTimeFilter, setLoginTimeFilter] = useState('');

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((searchValue, role, date, time) => {
      dispatch(
        searchAdminUsers({
          search_term: searchValue,
          role,
          login_date: date,
          login_time: time,
        })
      );
    }, 300),
    [dispatch]
  );

  // Initial load
  useEffect(() => {
    setLayoutConfig({
      title: "Admin Account & Roles",
      showBackButton: false,
      scrollable: false,
    });
  }, [setLayoutConfig]);

  // Handle search and filter changes
  useEffect(() => {
    if (searchTerm || roleFilter || loginDateFilter || loginTimeFilter) {
      debouncedSearch(searchTerm, roleFilter, loginDateFilter, loginTimeFilter);
    } else {
      dispatch(fetchAdmins());
    }
    return () => debouncedSearch.cancel();
  }, [searchTerm, roleFilter, loginDateFilter, loginTimeFilter, debouncedSearch, dispatch]);

  const handleApplyFilters = (isReset) => {
    if (isReset) {
      setRoleFilter('');
      setLoginDateFilter('');
      setLoginTimeFilter('');
      dispatch(fetchAdmins());
    } else {
      dispatch(
        searchAdminUsers({
          search_term: searchTerm,
          role: roleFilter,
          login_date: loginDateFilter,
          login_time: loginTimeFilter,
        })
      );
    }
  };

  // Batch Delete Handler
  const handleBatchDelete = () => {
    if (selectedUsers.length === 0) return;
    openModal("DELETE", { full_name: "selected admins", id: selectedUsers });
  };

  // Table Action Delete Handler
  const handleTableDelete = (admin) => {
    openModal("DELETE", admin);
  };

  // Modal open/close
  const openModal = (type, admin = null) => {
    setSelectedAdmin(admin);
    setModalType(type);
  };

  const closeModal = () => {
    setSelectedAdmin(null);
    setModalType(null);
  };

  // Confirm Delete Handler (single or batch)
  const handleDeleteAdmin = (adminIdOrArray) => {
    if (Array.isArray(adminIdOrArray)) {
      dispatch(deleteAdminAccounts(adminIdOrArray));
      setSelectedUsers([]);
    } else {
      dispatch(deleteAdminAccounts([adminIdOrArray]));
      setSelectedUsers((prev) => prev.filter((id) => id !== adminIdOrArray));
    }
    dispatch(fetchAdmins());
  };

  // Selection handlers
  const toggleUserSelection = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAllUsers = () => {
    setSelectedUsers((prev) =>
      prev.length === admins.length ? [] : admins.map((user) => user.id)
    );
  };

  if (loading) return <LoadingFallback />;
  if (error) return <ErrorFallback onRetry={() => dispatch(fetchAdmins())} />;

  return (
    <div>
      <main className=" flex-1 bg-[#FFFFFF] text-gray-800 p-4 rounded">
        <div className=" rounded-md bg-[#FFFFFF] p-4">

          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            {/* Left-aligned search (always visible) */}
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search By Name Or Role"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border border-gray-300 rounded text-sm px-2 py-1 pl-8 w-full focus:outline-none focus:ring-1 focus:ring-[#0B0C52]"
              />
              <IconSearch size={16} className="absolute left-2 top-2 text-gray-400" />
            </div>

            {/* Right-aligned buttons - dynamic based on selection */}
            <div className="flex items-center gap-2">
              {selectedUsers.length > 0 ? (
                <>
                  <span className="text-sm text-gray-700 whitespace-nowrap">
                    {selectedUsers.length} selected
                  </span>
                  <button
                    className="text-red-500 hover:text-red-700 flex items-center gap-1 text-sm px-3 py-1 rounded hover:bg-red-50"
                    onClick={handleBatchDelete}
                  >
                    <IconTrash size={16} />
                    Delete Selected
                  </button>
                  <button
                    className="text-gray-500 hover:text-gray-700 text-sm px-3 py-1 rounded hover:bg-gray-100"
                    onClick={() => setSelectedUsers([])}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <IconButton onClick={() => dispatch(fetchAdmins())}>
                    <IconRefresh size={18} />
                  </IconButton>
                  <IconButton onClick={() => setIsFilterModalOpen(true)}>
                    <IconFilter size={18} />
                  </IconButton>
                  <button
                    className="bg-green-500 text-white text-sm px-3 py-1 rounded hover:bg-green-600 flex items-center gap-1"
                    onClick={() => navigate('/admin/roles/new')}
                  >
                    <IconCirclePlus size={18} />
                    Add New Admin
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="overflow-x-auto h-[calc(100vh-250px)]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#F9F9FF] z-20 shadow-md">
                <tr className="bg-[#F9F9FF] text-[#120E43]">
                  <th className="px-4 py-2 w-10">
                    <input
                      type="checkbox"
                      checked={selectedUsers.length > 0 && selectedUsers.length === admins.length}
                      onChange={selectAllUsers}
                    />
                  </th>
                  <th className="px-4 py-2 text-left">Profile</th>
                  <th className="px-4 py-2 text-left">Admin Name</th>
                  <th className="px-4 py-2 text-left">Role</th>
                  <th className="px-4 py-2 text-left">Last Login</th>
                  <th className="px-4 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((admin) => (
                  <tr
                    key={admin.id}
                    className={`border-b border-gray-200 hover:bg-gray-50 ${selectedUsers.includes(admin.id) ? 'bg-blue-50' : ''
                      }`}
                  >
                    <td className="px-4 py-2">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(admin.id)}
                        onChange={() => toggleUserSelection(admin.id)}
                      />
                    </td>
                    <td className="px-4 py-2">
                      {admin.profile_picture ? (
                        <img
                          src={admin.profile_picture}
                          alt="Profile"
                          className="w-8 h-8 rounded-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                          }}
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-300" />
                      )}
                    </td>
                    <td className="px-4 py-2">{admin.full_name || 'N/A'}</td>
                    <td className="px-4 py-2">{admin.roles}</td>
                    <td className="px-4 py-2">{admin.last_login}</td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <button
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          onClick={() => navigate(`/admin/roles/${admin.id}`)}
                        >
                          <IconPencil size={16} />
                          Edit
                        </button>
                        <button
                          className="text-red-500 hover:text-red-700 flex items-center gap-1"
                          onClick={() => handleTableDelete(admin)}
                        >
                          <IconTrash size={16} />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {admins.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center py-4 text-gray-500">
                      No admins found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end text-sm text-gray-600 bg-[#F9F9FF] p-2 rounded-md">
            <span className="text-[#0B0C52]">
              Total Admins: <strong>{admins.length}</strong>
            </span>
          </div>

        </div>
      </main>

      {/* Modals */}
      {modalType === 'DELETE' && selectedAdmin && (
        <DeleteAdminModal
          admin={selectedAdmin}
          onClose={closeModal}
          onConfirm={handleDeleteAdmin}
        />
      )}

      {isFilterModalOpen && (
        <FilterModal
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          role={roleFilter}
          setRole={setRoleFilter}
          loginDate={loginDateFilter}
          setLoginDate={setLoginDateFilter}
          loginTime={loginTimeFilter}
          setLoginTime={setLoginTimeFilter}
          onApply={handleApplyFilters}
        />
      )}
    </div>
  );
}

export default AdminAccountRoles;